import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSsoConnections } from '../../hooks/useSsoConnections'
import { useApplications } from '../../hooks/useApplications'
import type { SsoProtocol, Application } from '../../types/database'
import {
  VStack,
  HStack,
  Card,
  Heading,
  Text,
  Button,
  IconButton,
  Input,
  Textarea,
  Switch,
  Select,
  Badge,
  Banner,
  Animate,
  Tabs,
  TabPanels,
  TabPanel,
  ConfirmDialog
} from 'wss3-forge'
import {
  ArrowLeft20Regular,
  Save20Regular,
  Delete20Regular,
  Eye20Regular,
  EyeOff20Regular,
  Checkmark20Regular,
  Dismiss20Regular,
  Key20Regular,
  Shield20Regular,
  Settings20Regular,
  Play20Regular
} from '@fluentui/react-icons'

export function SsoConnectionForm() {
  const navigate = useNavigate()
  const { orgId, connectionId } = useParams()
  const isEdit = connectionId !== 'new' && Boolean(connectionId)

  const { getConnection, createConnection, updateConnection, deleteConnection, testConnection } = useSsoConnections()
  const { applications } = useApplications()

  const [protocol, setProtocol] = useState<SsoProtocol>('saml')
  const [appId, setAppId] = useState('')
  const [isActive, setIsActive] = useState(true)

  // SAML config
  const [samlIdpEntityId, setSamlIdpEntityId] = useState('')
  const [samlIdpSsoUrl, setSamlIdpSsoUrl] = useState('')
  const [samlIdpCertificate, setSamlIdpCertificate] = useState('')

  // OIDC config
  const [oidcIssuer, setOidcIssuer] = useState('')
  const [oidcClientId, setOidcClientId] = useState('')
  const [oidcClientSecret, setOidcClientSecret] = useState('')
  const [oidcAuthorizationUrl, setOidcAuthorizationUrl] = useState('')
  const [oidcTokenUrl, setOidcTokenUrl] = useState('')
  const [oidcUserinfoUrl, setOidcUserinfoUrl] = useState('')

  // Options
  const [jitProvisioning, setJitProvisioning] = useState(true)
  const [autoUpdateProfile, setAutoUpdateProfile] = useState(true)
  const [defaultRole, setDefaultRole] = useState('user')

  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [activeTab, setActiveTab] = useState('config')
  const [showSecret, setShowSecret] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)
  const [testing, setTesting] = useState(false)

  useEffect(() => {
    if (isEdit && connectionId) {
      loadConnectionData()
    }
  }, [isEdit, connectionId])

  const loadConnectionData = async () => {
    if (!connectionId) return

    const conn = await getConnection(connectionId)
    if (conn) {
      setProtocol(conn.protocol)
      setAppId(conn.app_id)
      setIsActive(conn.is_active)

      // SAML
      setSamlIdpEntityId(conn.saml_idp_entity_id || '')
      setSamlIdpSsoUrl(conn.saml_idp_sso_url || '')
      setSamlIdpCertificate(conn.saml_idp_certificate || '')

      // OIDC
      setOidcIssuer(conn.oidc_issuer || '')
      setOidcClientId(conn.oidc_client_id || '')
      setOidcClientSecret(conn.oidc_client_secret || '')
      setOidcAuthorizationUrl(conn.oidc_authorization_url || '')
      setOidcTokenUrl(conn.oidc_token_url || '')
      setOidcUserinfoUrl(conn.oidc_userinfo_url || '')

      // Options
      setJitProvisioning(conn.jit_provisioning)
      setAutoUpdateProfile(conn.auto_update_profile)
      setDefaultRole(conn.default_role || 'user')
    }
  }

  const handleSubmit = async () => {
    const newErrors: Record<string, string> = {}

    if (!appId && !isEdit) {
      newErrors.appId = 'Sélectionnez une application'
    }

    if (protocol === 'saml') {
      if (!samlIdpEntityId.trim()) {
        newErrors.samlIdpEntityId = 'L\'Entity ID est requis'
      }
      if (!samlIdpSsoUrl.trim()) {
        newErrors.samlIdpSsoUrl = 'L\'URL SSO est requise'
      }
      if (!samlIdpCertificate.trim()) {
        newErrors.samlIdpCertificate = 'Le certificat est requis'
      }
    } else if (protocol === 'oidc') {
      if (!oidcClientId.trim()) {
        newErrors.oidcClientId = 'Le Client ID est requis'
      }
      if (!oidcClientSecret.trim()) {
        newErrors.oidcClientSecret = 'Le Client Secret est requis'
      }
      if (!oidcAuthorizationUrl.trim()) {
        newErrors.oidcAuthorizationUrl = 'L\'URL d\'autorisation est requise'
      }
      if (!oidcTokenUrl.trim()) {
        newErrors.oidcTokenUrl = 'L\'URL de token est requise'
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setErrors({})
    setSaving(true)

    const data = {
      protocol,
      is_active: isActive,
      // SAML
      saml_idp_entity_id: protocol === 'saml' ? samlIdpEntityId : undefined,
      saml_idp_sso_url: protocol === 'saml' ? samlIdpSsoUrl : undefined,
      saml_idp_certificate: protocol === 'saml' ? samlIdpCertificate : undefined,
      // OIDC
      oidc_issuer: protocol === 'oidc' ? oidcIssuer : undefined,
      oidc_client_id: protocol === 'oidc' ? oidcClientId : undefined,
      oidc_client_secret: protocol === 'oidc' ? oidcClientSecret : undefined,
      oidc_authorization_url: protocol === 'oidc' ? oidcAuthorizationUrl : undefined,
      oidc_token_url: protocol === 'oidc' ? oidcTokenUrl : undefined,
      oidc_userinfo_url: protocol === 'oidc' ? oidcUserinfoUrl : undefined,
      // Options
      jit_provisioning: jitProvisioning,
      auto_update_profile: autoUpdateProfile,
      default_role: defaultRole
    }

    if (isEdit && connectionId) {
      const success = await updateConnection(connectionId, data)
      if (success) {
        navigate(`/platform/organizations/${orgId}`)
      } else {
        setErrors({ general: 'Échec de la mise à jour' })
      }
    } else {
      const result = await createConnection({
        ...data,
        organization_id: orgId!,
        app_id: appId
      })
      if (result) {
        navigate(`/platform/organizations/${orgId}`)
      } else {
        setErrors({ general: 'Échec de la création' })
      }
    }

    setSaving(false)
  }

  const handleTest = async () => {
    if (!connectionId || !isEdit) return

    setTesting(true)
    setTestResult(null)

    const result = await testConnection(connectionId)
    setTestResult(result)
    setTesting(false)
  }

  const handleDelete = async () => {
    if (!connectionId) return
    const success = await deleteConnection(connectionId)
    if (success) {
      navigate(`/platform/organizations/${orgId}`)
    }
    setShowDeleteConfirm(false)
  }

  // Get SP Entity ID for display
  const spEntityId = `https://account.webba-creative.com/saml/${orgId}`
  const acsUrl = `https://account.webba-creative.com/auth/sso/callback`
  const oidcRedirectUri = `https://account.webba-creative.com/auth/sso/callback`

  return (
    <div className="page-content">
      <VStack gap="lg">
        <Animate type="fadeIn">
          <HStack gap="md" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <HStack gap="md" style={{ alignItems: 'center' }}>
              <IconButton
                icon={<ArrowLeft20Regular />}
                onClick={() => navigate(`/platform/organizations/${orgId}`)}
              />
              <div>
                <Heading level={2}>{isEdit ? 'Modifier la connexion SSO' : 'Nouvelle connexion SSO'}</Heading>
                <Text color="muted">
                  {isEdit ? 'Modifiez la configuration SSO' : 'Configurez SAML ou OIDC pour cette organisation'}
                </Text>
              </div>
            </HStack>
            {isEdit && (
              <Button
                variant="danger"
                icon={<Delete20Regular />}
                onClick={() => setShowDeleteConfirm(true)}
              >
                Supprimer
              </Button>
            )}
          </HStack>
        </Animate>

        {errors.general && (
          <Banner variant="error" title="Erreur">
            {errors.general}
          </Banner>
        )}

        <Animate type="slideInUp" delay={50}>
          <Tabs
            tabs={[
              { id: 'config', label: 'Configuration', icon: <Key20Regular /> },
              { id: 'options', label: 'Options', icon: <Settings20Regular /> },
              ...(isEdit ? [{ id: 'info', label: 'Informations SP', icon: <Shield20Regular /> }] : [])
            ]}
            active={activeTab}
            onChange={setActiveTab}
            stretchLine
          />

          <div style={{ marginTop: '1.5rem' }}>
            <TabPanels active={activeTab}>
              {/* Configuration Tab */}
              <TabPanel id="config" active={activeTab}>
                <VStack gap="lg">
                  {/* Application selection (only for new) */}
                  {!isEdit && (
                    <Card padding="lg">
                      <VStack gap="md">
                        <Heading level={5}>Application</Heading>
                        <Select
                          label="Sélectionnez l'application"
                          value={appId}
                          onChange={(v) => {
                            setAppId(v as string)
                            if (errors.appId) setErrors(prev => ({ ...prev, appId: '' }))
                          }}
                          options={applications.map((app: Application) => ({
                            value: app.id,
                            label: app.name
                          }))}
                          error={errors.appId}
                        />
                      </VStack>
                    </Card>
                  )}

                  {/* Protocol selection */}
                  <Card padding="lg">
                    <VStack gap="md">
                      <Heading level={5}>Protocole</Heading>

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '1rem'
                      }}>
                        <Card
                          padding="lg"
                          variant={protocol === 'saml' ? 'default' : 'outlined'}
                          hoverable
                          onClick={() => setProtocol('saml')}
                          style={{
                            cursor: 'pointer',
                            borderColor: protocol === 'saml' ? 'var(--brand-primary)' : undefined
                          }}
                        >
                          <VStack gap="sm" style={{ alignItems: 'center', textAlign: 'center' }}>
                            <Badge size="lg">SAML 2.0</Badge>
                            <Text size="sm" color="muted">
                              Standard d'entreprise pour SSO. Compatible avec Azure AD, Okta, OneLogin, etc.
                            </Text>
                          </VStack>
                        </Card>

                        <Card
                          padding="lg"
                          variant={protocol === 'oidc' ? 'default' : 'outlined'}
                          hoverable
                          onClick={() => setProtocol('oidc')}
                          style={{
                            cursor: 'pointer',
                            borderColor: protocol === 'oidc' ? 'var(--brand-primary)' : undefined
                          }}
                        >
                          <VStack gap="sm" style={{ alignItems: 'center', textAlign: 'center' }}>
                            <Badge size="lg">OpenID Connect</Badge>
                            <Text size="sm" color="muted">
                              Protocole moderne basé sur OAuth 2.0. Compatible avec la plupart des IdP.
                            </Text>
                          </VStack>
                        </Card>
                      </div>
                    </VStack>
                  </Card>

                  {/* SAML Configuration */}
                  {protocol === 'saml' && (
                    <Card padding="lg">
                      <VStack gap="md">
                        <Heading level={5}>Configuration SAML</Heading>

                        <div>
                          <Input
                            label="Entity ID de l'IdP"
                            placeholder="https://login.microsoftonline.com/{tenant}/saml2"
                            value={samlIdpEntityId}
                            onChange={(v) => {
                              setSamlIdpEntityId(v as string)
                              if (errors.samlIdpEntityId) setErrors(prev => ({ ...prev, samlIdpEntityId: '' }))
                            }}
                            error={errors.samlIdpEntityId}
                          />
                          <Text size="xs" color="muted" style={{ marginTop: '0.25rem' }}>
                            Identifiant unique de votre Identity Provider
                          </Text>
                        </div>

                        <div>
                          <Input
                            label="URL SSO (Single Sign-On)"
                            placeholder="https://login.microsoftonline.com/{tenant}/saml2"
                            value={samlIdpSsoUrl}
                            onChange={(v) => {
                              setSamlIdpSsoUrl(v as string)
                              if (errors.samlIdpSsoUrl) setErrors(prev => ({ ...prev, samlIdpSsoUrl: '' }))
                            }}
                            error={errors.samlIdpSsoUrl}
                          />
                          <Text size="xs" color="muted" style={{ marginTop: '0.25rem' }}>
                            URL où les utilisateurs seront redirigés pour s'authentifier
                          </Text>
                        </div>

                        <div>
                          <Textarea
                            label="Certificat X.509 (PEM)"
                            placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                            value={samlIdpCertificate}
                            onChange={(v) => {
                              setSamlIdpCertificate(v as string)
                              if (errors.samlIdpCertificate) setErrors(prev => ({ ...prev, samlIdpCertificate: '' }))
                            }}
                            rows={6}
                            error={errors.samlIdpCertificate}
                          />
                          <Text size="xs" color="muted" style={{ marginTop: '0.25rem' }}>
                            Le certificat public de votre IdP pour valider les assertions SAML
                          </Text>
                        </div>
                      </VStack>
                    </Card>
                  )}

                  {/* OIDC Configuration */}
                  {protocol === 'oidc' && (
                    <Card padding="lg">
                      <VStack gap="md">
                        <Heading level={5}>Configuration OpenID Connect</Heading>

                        <div>
                          <Input
                            label="Issuer (optionnel)"
                            placeholder="https://login.microsoftonline.com/{tenant}/v2.0"
                            value={oidcIssuer}
                            onChange={(v) => setOidcIssuer(v as string)}
                          />
                          <Text size="xs" color="muted" style={{ marginTop: '0.25rem' }}>
                            URL de l'émetteur. Si fourni, on tentera de récupérer la configuration automatiquement.
                          </Text>
                        </div>

                        <Input
                          label="Client ID"
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          value={oidcClientId}
                          onChange={(v) => {
                            setOidcClientId(v as string)
                            if (errors.oidcClientId) setErrors(prev => ({ ...prev, oidcClientId: '' }))
                          }}
                          error={errors.oidcClientId}
                        />

                        <div>
                          <Text size="sm" weight="medium" style={{ marginBottom: '0.5rem' }}>
                            Client Secret
                          </Text>
                          <HStack gap="sm">
                            <Input
                              type={showSecret ? 'text' : 'password'}
                              placeholder="••••••••••••••••"
                              value={oidcClientSecret}
                              onChange={(v) => {
                                setOidcClientSecret(v as string)
                                if (errors.oidcClientSecret) setErrors(prev => ({ ...prev, oidcClientSecret: '' }))
                              }}
                              error={errors.oidcClientSecret}
                            />
                            <IconButton
                              icon={showSecret ? <EyeOff20Regular /> : <Eye20Regular />}
                              onClick={() => setShowSecret(!showSecret)}
                            />
                          </HStack>
                        </div>

                        <Input
                          label="Authorization URL"
                          placeholder="https://login.microsoftonline.com/{tenant}/oauth2/v2.0/authorize"
                          value={oidcAuthorizationUrl}
                          onChange={(v) => {
                            setOidcAuthorizationUrl(v as string)
                            if (errors.oidcAuthorizationUrl) setErrors(prev => ({ ...prev, oidcAuthorizationUrl: '' }))
                          }}
                          error={errors.oidcAuthorizationUrl}
                        />

                        <Input
                          label="Token URL"
                          placeholder="https://login.microsoftonline.com/{tenant}/oauth2/v2.0/token"
                          value={oidcTokenUrl}
                          onChange={(v) => {
                            setOidcTokenUrl(v as string)
                            if (errors.oidcTokenUrl) setErrors(prev => ({ ...prev, oidcTokenUrl: '' }))
                          }}
                          error={errors.oidcTokenUrl}
                        />

                        <div>
                          <Input
                            label="Userinfo URL (optionnel)"
                            placeholder="https://graph.microsoft.com/oidc/userinfo"
                            value={oidcUserinfoUrl}
                            onChange={(v) => setOidcUserinfoUrl(v as string)}
                          />
                          <Text size="xs" color="muted" style={{ marginTop: '0.25rem' }}>
                            URL pour récupérer les informations de l'utilisateur
                          </Text>
                        </div>
                      </VStack>
                    </Card>
                  )}

                  {/* Status */}
                  <Card padding="lg">
                    <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <Text weight="medium">Statut de la connexion</Text>
                        <Text size="sm" color="muted">
                          {isActive ? 'Les utilisateurs peuvent se connecter via ce SSO' : 'La connexion SSO est désactivée'}
                        </Text>
                      </div>
                      <Switch
                        checked={isActive}
                        onChange={setIsActive}
                      />
                    </HStack>
                  </Card>
                </VStack>
              </TabPanel>

              {/* Options Tab */}
              <TabPanel id="options" active={activeTab}>
                <Card padding="lg">
                  <VStack gap="lg">
                    <Heading level={5}>Options de provisionnement</Heading>

                    <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <Text weight="medium">Provisionnement automatique (JIT)</Text>
                        <Text size="sm" color="muted">
                          Créer automatiquement un compte utilisateur lors de la première connexion SSO
                        </Text>
                      </div>
                      <Switch
                        checked={jitProvisioning}
                        onChange={setJitProvisioning}
                      />
                    </HStack>

                    <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <Text weight="medium">Mise à jour automatique du profil</Text>
                        <Text size="sm" color="muted">
                          Mettre à jour le profil utilisateur à chaque connexion avec les données de l'IdP
                        </Text>
                      </div>
                      <Switch
                        checked={autoUpdateProfile}
                        onChange={setAutoUpdateProfile}
                      />
                    </HStack>

                    <div>
                      <Select
                        label="Rôle par défaut"
                        value={defaultRole}
                        onChange={(v) => setDefaultRole(v as string)}
                        options={[
                          { value: 'user', label: 'Utilisateur' },
                          { value: 'member', label: 'Membre' },
                          { value: 'admin', label: 'Administrateur' }
                        ]}
                      />
                      <Text size="xs" color="muted" style={{ marginTop: '0.25rem' }}>
                        Rôle attribué aux nouveaux utilisateurs créés via SSO
                      </Text>
                    </div>
                  </VStack>
                </Card>
              </TabPanel>

              {/* SP Info Tab (edit only) */}
              {isEdit && (
                <TabPanel id="info" active={activeTab}>
                  <VStack gap="lg">
                    <Card padding="lg">
                      <VStack gap="md">
                        <Heading level={5}>Informations du Service Provider</Heading>
                        <Text size="sm" color="muted">
                          Utilisez ces informations pour configurer Webba ID dans votre Identity Provider.
                        </Text>

                        {protocol === 'saml' && (
                          <>
                            <div style={{
                              backgroundColor: 'var(--bg-tertiary)',
                              borderRadius: 'var(--radius-md)',
                              padding: '1rem'
                            }}>
                              <VStack gap="md">
                                <div>
                                  <Text size="sm" color="muted">SP Entity ID (Audience)</Text>
                                  <code style={{
                                    display: 'block',
                                    padding: '0.5rem',
                                    backgroundColor: 'var(--bg-secondary)',
                                    borderRadius: 'var(--radius-sm)',
                                    fontSize: '0.8rem',
                                    marginTop: '0.25rem'
                                  }}>
                                    {spEntityId}
                                  </code>
                                </div>

                                <div>
                                  <Text size="sm" color="muted">ACS URL (Assertion Consumer Service)</Text>
                                  <code style={{
                                    display: 'block',
                                    padding: '0.5rem',
                                    backgroundColor: 'var(--bg-secondary)',
                                    borderRadius: 'var(--radius-sm)',
                                    fontSize: '0.8rem',
                                    marginTop: '0.25rem'
                                  }}>
                                    {acsUrl}
                                  </code>
                                </div>

                                <div>
                                  <Text size="sm" color="muted">Name ID Format</Text>
                                  <code style={{
                                    display: 'block',
                                    padding: '0.5rem',
                                    backgroundColor: 'var(--bg-secondary)',
                                    borderRadius: 'var(--radius-sm)',
                                    fontSize: '0.8rem',
                                    marginTop: '0.25rem'
                                  }}>
                                    urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress
                                  </code>
                                </div>
                              </VStack>
                            </div>

                            <Banner variant="info" title="Configuration de l'IdP">
                              Configurez votre IdP (Azure AD, Okta, etc.) avec ces valeurs. L'attribut email est obligatoire.
                            </Banner>
                          </>
                        )}

                        {protocol === 'oidc' && (
                          <>
                            <div style={{
                              backgroundColor: 'var(--bg-tertiary)',
                              borderRadius: 'var(--radius-md)',
                              padding: '1rem'
                            }}>
                              <VStack gap="md">
                                <div>
                                  <Text size="sm" color="muted">Redirect URI (Callback URL)</Text>
                                  <code style={{
                                    display: 'block',
                                    padding: '0.5rem',
                                    backgroundColor: 'var(--bg-secondary)',
                                    borderRadius: 'var(--radius-sm)',
                                    fontSize: '0.8rem',
                                    marginTop: '0.25rem'
                                  }}>
                                    {oidcRedirectUri}
                                  </code>
                                </div>

                                <div>
                                  <Text size="sm" color="muted">Scopes requis</Text>
                                  <HStack gap="xs" style={{ marginTop: '0.25rem' }}>
                                    <Badge size="sm">openid</Badge>
                                    <Badge size="sm">email</Badge>
                                    <Badge size="sm">profile</Badge>
                                  </HStack>
                                </div>
                              </VStack>
                            </div>

                            <Banner variant="info" title="Configuration de l'IdP">
                              Ajoutez l'URI de redirection dans votre application OIDC et activez les scopes openid, email et profile.
                            </Banner>
                          </>
                        )}
                      </VStack>
                    </Card>

                    {/* Test Connection */}
                    <Card padding="lg">
                      <VStack gap="md">
                        <Heading level={5}>Tester la connexion</Heading>
                        <Text size="sm" color="muted">
                          Vérifiez que la configuration est valide avant d'activer le SSO.
                        </Text>

                        <HStack gap="md">
                          <Button
                            icon={<Play20Regular />}
                            onClick={handleTest}
                            loading={testing}
                          >
                            Tester la configuration
                          </Button>
                        </HStack>

                        {testResult && (
                          <Banner
                            variant={testResult.success ? 'success' : 'error'}
                            title={testResult.success ? 'Configuration valide' : 'Erreur de configuration'}
                            icon={testResult.success ? <Checkmark20Regular /> : <Dismiss20Regular />}
                          >
                            {testResult.message}
                          </Banner>
                        )}
                      </VStack>
                    </Card>
                  </VStack>
                </TabPanel>
              )}
            </TabPanels>
          </div>

          <HStack gap="md" style={{ justifyContent: 'flex-end', paddingTop: '1rem' }}>
            <Button
              variant="secondary"
              onClick={() => navigate(`/platform/organizations/${orgId}`)}
            >
              Annuler
            </Button>
            <Button
              icon={<Save20Regular />}
              loading={saving}
              onClick={handleSubmit}
            >
              {isEdit ? 'Enregistrer' : 'Créer la connexion'}
            </Button>
          </HStack>
        </Animate>
      </VStack>

      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Supprimer la connexion SSO"
        description="Êtes-vous sûr de vouloir supprimer cette connexion SSO ? Les utilisateurs ne pourront plus se connecter via ce SSO."
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
        onConfirm={handleDelete}
      />
    </div>
  )
}
